import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { merge } from 'lodash';
import {
    first,
    forkJoin,
    from,
    mergeMap,
    Observable,
    of,
    ReplaySubject,
    take,
} from 'rxjs';
import { Gym, User } from './user.types';

@Injectable({
    providedIn: 'root',
})
export class GymService {
    private _gym: ReplaySubject<Gym> = new ReplaySubject<Gym>(1);

    constructor(private fireStore: AngularFirestore) {}

    get gym$(): Observable<Gym> {
        return this._gym.asObservable();
    }

    set user(currentUser: User) {
        if (!currentUser) {
            this._gym.next(null);
            return;
        }
        this.fireStore
            .collection('gyms', (ref) =>
                ref.where('roles.admin', 'array-contains', currentUser.uid)
            )
            .get()
            .pipe(first())
            .subscribe((snapshot) => {
                if (!snapshot.docs[0]) {
                    this._gym.next(null);
                    return;
                }
                const gym = snapshot.docs[0].data() as Gym;
                this._gym.next({ ...gym, uid: snapshot.docs[0].id });
            });
    }

    createGym(userUid: string, name: string): Observable<string> {
        const newGym: Gym = {
            name: name,
            roles: { admin: [userUid] },
        };
        const docRef = this.fireStore.collection('gyms').doc();
        return from(docRef.set(newGym)).pipe(
            mergeMap(() => of(docRef.ref.id))
        );
    }

    save(
        gymId: string,
        data: Partial<Gym>,
        cachedData?: Partial<Gym>
    ): Observable<void> {
        const saveGym = from(
            this.fireStore
                .collection('gyms')
                .doc(gymId)
                .set(data, { merge: true })
        );
        return forkJoin([this.gym$.pipe(take(1)), saveGym]).pipe(
            mergeMap(([gym]) =>
                of(this._gym.next(merge(gym, cachedData || data)))
            )
        );
    }

    getGymById(gymId: string): Observable<Gym> {
        return this.fireStore
            .collection('gyms')
            .doc(gymId)
            .get()
            .pipe(
                first(),
                mergeMap((doc) => {
                    if (!doc.exists) {
                        return of(null);
                    }
                    return of({ ...doc.data() as Gym, uid: doc.id });
                })
            );
    }

    updateGym(gymId: string, data: Partial<Gym>): Observable<void> {
        return this.save(gymId, data);
    }
}
